import { useEffect, useState } from 'react';
import type { CharacterResponse } from '../types';
import { fetchCharacterByKey } from '../lib/api';
import { classColor, factionColor, specOf } from '../lib/wow';
import { averageItemLevel } from '../lib/ilvl';

interface Props {
  keyA: string;
  keyB: string;
  onExit: () => void;
}

interface Loaded { a: CharacterResponse; b: CharacterResponse; }

const ROWS: Array<{ label: string; pick: (c: CharacterResponse) => number | string | undefined; suffix?: string }> = [
  { label: 'Level',         pick: (c) => c.level },
  { label: 'Class',         pick: (c) => c.snapshot.profile.class },
  { label: 'Spec',          pick: (c) => specOf(c.snapshot)?.name ?? '—' },
  { label: 'Race',          pick: (c) => c.snapshot.profile.race },
  { label: 'Faction',       pick: (c) => c.snapshot.profile.faction },
  { label: 'Realm',         pick: (c) => c.snapshot.profile.realm },
  { label: 'iLvl (avg)',    pick: (c) => averageItemLevel(c.snapshot) ?? '—' },
  { label: 'HP max',        pick: (c) => c.snapshot.vitals?.hpMax },
  { label: 'Strength',      pick: (c) => c.snapshot.stats?.str },
  { label: 'Agility',       pick: (c) => c.snapshot.stats?.agi },
  { label: 'Stamina',       pick: (c) => c.snapshot.stats?.sta },
  { label: 'Intellect',     pick: (c) => c.snapshot.stats?.int },
  { label: 'Spirit',        pick: (c) => c.snapshot.stats?.spi },
  { label: 'Attack Power',  pick: (c) => c.snapshot.stats?.attackPower },
  { label: 'Spell Power',   pick: (c) => c.snapshot.stats?.spellPower },
  { label: 'Melee Crit %',  pick: (c) => c.snapshot.stats?.critMeleePct, suffix: '%' },
  { label: 'Spell Crit %',  pick: (c) => c.snapshot.stats?.critSpellPct, suffix: '%' },
  { label: 'Armor',         pick: (c) => c.snapshot.stats?.armor },
  { label: 'Defense',       pick: (c) => c.snapshot.stats?.defense },
];

export function CompareView({ keyA, keyB, onExit }: Props) {
  const [data, setData] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          fetchCharacterByKey(keyA),
          fetchCharacterByKey(keyB),
        ]);
        if (!cancelled) setData({ a, b });
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load comparison');
      }
    })();
    return () => { cancelled = true; };
  }, [keyA, keyB]);

  if (error) return <div className="compare-view error">Error: {error}</div>;
  if (!data) return <div className="compare-view loading">Loading comparison…</div>;

  const { a, b } = data;
  const colorA = classColor(a.classFile);
  const colorB = classColor(b.classFile);

  return (
    <div className="compare-view">
      <button type="button" className="compare-back" onClick={onExit}>← Exit comparison</button>

      <div className="compare-grid">
        <div className="compare-cell compare-head" style={{ borderTopColor: factionColor(a.faction) }}>
          <div className="compare-name" style={{ color: colorA }}>{a.name}</div>
          <div className="compare-sub">L{a.level} · {a.snapshot.profile.realm}</div>
        </div>
        <div className="compare-cell compare-head-mid">vs</div>
        <div className="compare-cell compare-head" style={{ borderTopColor: factionColor(b.faction) }}>
          <div className="compare-name" style={{ color: colorB }}>{b.name}</div>
          <div className="compare-sub">L{b.level} · {b.snapshot.profile.realm}</div>
        </div>

        {ROWS.map((row) => {
          const va = row.pick(a);
          const vb = row.pick(b);
          const numA = typeof va === 'number' ? va : null;
          const numB = typeof vb === 'number' ? vb : null;
          let dA = '', dB = '';
          if (numA !== null && numB !== null && numA !== numB) {
            if (numA > numB) dA = '+' + (numA - numB).toFixed(numA % 1 ? 2 : 0);
            else             dB = '+' + (numB - numA).toFixed(numB % 1 ? 2 : 0);
          }
          return (
            <FragmentRow
              key={row.label}
              label={row.label}
              left={fmt(va, row.suffix)}
              middle={null}
              right={fmt(vb, row.suffix)}
              deltaLeft={dA}
              deltaRight={dB}
            />
          );
        })}
      </div>
    </div>
  );
}

function fmt(v: any, suffix?: string): string {
  if (v === undefined || v === null || v === '') return '—';
  const s = typeof v === 'number' ? (Math.round(v * 100) / 100).toString() : String(v);
  return suffix ? `${s}${suffix}` : s;
}

function FragmentRow({ label, left, right, deltaLeft, deltaRight }:
  { label: string; left: string; middle: any; right: string; deltaLeft?: string; deltaRight?: string }) {
  return (
    <>
      <div className="compare-cell compare-val compare-val-left">
        {left}
        {deltaLeft && <span className="compare-delta good"> {deltaLeft}</span>}
      </div>
      <div className="compare-cell compare-row-label">{label}</div>
      <div className="compare-cell compare-val compare-val-right">
        {right}
        {deltaRight && <span className="compare-delta good"> {deltaRight}</span>}
      </div>
    </>
  );
}
